import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useCreateBlockNote } from '@blocknote/react';
import { BlockNoteView } from '@blocknote/shadcn';
import '@blocknote/shadcn/style.css';
import './editor.less';
import { BlockNoteSchema, defaultBlockSpecs } from '@blocknote/core';
import { owuploadCustomRequest } from '../../thunks/file_thunks';
import html2pdf from 'html2pdf.js';
import { Button, Dropdown, Menu, message, Input } from 'antd';
import { inlineImagesAsBase64, renderAttachmentsAsLinks } from '../../utils/ImageUtils';
import { withUserType } from '../withUserType';
import {
	DownOutlined,
	FilePdfOutlined,
	LinkOutlined,
	RedoOutlined,
	ShareAltOutlined,
	UndoOutlined,
} from '@ant-design/icons';
import { debounce } from '../../utils/PerformanceUtils';
import { getProtectedImageUriGeneric } from '../../utils/FileAccessUtils';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { ContactAvatar } from '../contact_avatar/ContactAvatar';
import { withCurrentUser } from '../../hooks/withCurrentUser';
import { stringArrToList, userRoleTextFormatter } from '../../utils/DataFormatterUtils';
import moment from 'moment';
import { Document, EditorProps } from '../../types/documentTypes';
import { BUYER_ROLES, SUPPLIER_ROLES } from '../roles/roles';
import { isUserAllowedToAccess, PERMISSION_NAMES } from '../../utils/AuthUtils';
import { withCompanyConfig } from '../../hooks/withCompanyConfig';
import { ROLE_TYPES } from '../../utils/DataConstants';

const schema = BlockNoteSchema.create({
	blockSpecs: {
		...defaultBlockSpecs,
	},
});

const BlockNoteEditor: React.FC<EditorProps> = ({
	userType,
	document,
	updateDocument,
	currentUser,
	editable = true,
	showToolbar = true,
	companyConfig,
}) => {
	const editorRef = useRef(null);
	const [isSaving, setIsSaving] = useState(false);
	const [lastSaved, setLastSaved] = useState<Date | null>(
		nullSafeGetOrElse('updatedAt', document, null)
	);
	const previousContentRef = useRef<string | null>(null);
	const [title, setTitle] = useState(nullSafeGetOrElse('title', document, ''));

	const isNewDocument = useMemo(() => {
		const blocks = nullSafeGet('blocks', document);
		return !blocks || (Array.isArray(blocks) && blocks.length === 0) || blocks === '';
	}, [document]);

	const editor = useCreateBlockNote({
		schema,
		initialContent: nullSafeGetOrElse('blocks', document, ''),
		uploadFile: async (file: File) => {
			return new Promise((resolve, reject) => {
				const customRequest = {
					file,
					onError: (err: any) => reject(err),
					onSuccess: ({ data }: any) => {
						resolve(getProtectedImageUriGeneric(data.fileId, data.fileName));
					},
					onProgress: () => {},
					userType,
				};
				owuploadCustomRequest(customRequest);
			});
		},
	});

	const exportPDF = async () => {
		const element = editorRef.current;
		if (!element) return;

		await inlineImagesAsBase64(element);
		await renderAttachmentsAsLinks(element);

		html2pdf()
			.from(element)
			.set({
				margin: 10,
				filename: title || 'document.pdf',
				html2canvas: { scale: 2, useCORS: true },
				jsPDF: { unit: 'mm', format: 'a4' },
			})
			.save();
	};
	const handleCopyLink = async () => {
		try {
			await navigator.clipboard.writeText(window.location.href);
			message.success('Link copied to clipboard!');
		} catch {
			message.error('Failed to copy link');
		}
	};
	const saveContent = (titleToSave?: string) => {
		setIsSaving(true);
		const blocks = editor.document;

		const contentStr = JSON.stringify(blocks);
		const originalTitle = nullSafeGetOrElse('title', document, '');

		// Check if either content or title has changed
		if (previousContentRef.current === contentStr && originalTitle === titleToSave) {
			setIsSaving(false);
			return;
		}

		const documentData: Document = { blocks, title: titleToSave, updatedAt: new Date() };
		updateDocument(documentData)
			.then(() => {
				previousContentRef.current = contentStr;
				setLastSaved(new Date());
			})
			.catch((err) => {
				console.error('Error saving content:', err);
				message.error('Failed to save content');
			})
			.finally(() => {
				setIsSaving(false);
			});
	};

	const debouncedSave = useRef(debounce(saveContent, 2000)).current;

	useEffect(() => {
		if (!editor) return;

		const unsubscribe = editor.onEditorContentChange(() => {
			debouncedSave(title);
		});

		return () => {
			if (typeof unsubscribe === 'function') {
				unsubscribe();
			}
		};
	}, [editor, debouncedSave, title]);

	return (
		<div className="min-h-screen bg-white pb-8">
			<div className="">
				{showToolbar ? (
					<div
						className="flex flex-row flex-wrap items-center justify-between gap-y-2 px-6 py-4"
						style={{ borderBottom: '1px solid #E5E5E5' }}
					>
						<div className="flex items-center space-x-4 text-sm text-gray-500">
							<Button disabled={!editable} onClick={() => editor.undo()} icon={<UndoOutlined />}>
								Undo
							</Button>
							<Button disabled={!editable} onClick={() => editor.redo()} icon={<RedoOutlined />}>
								Redo
							</Button>
						</div>
						<div className=" flex items-center justify-end">
							{isSaving ? (
								<div className="bodyregular mr-4">Saving...</div>
							) : lastSaved ? (
								<div className="bodyregular mr-4">Edited {moment(lastSaved).format('hh:mm A')}</div>
							) : null}
							<Dropdown
								overlay={
									<Menu>
										{(userType === ROLE_TYPES.SUPPLIER ? SUPPLIER_ROLES : BUYER_ROLES).map(
											(role) => (
												<Menu.Item key={role}>
													<div className="flex flex-row items-center justify-between">
														<span>{userRoleTextFormatter(role)}</span>
														<span className="text-sm text-gray-500">
															Can{' '}
															{isUserAllowedToAccess(userType)(
																PERMISSION_NAMES.MODIFY_LOCATION_DOCUMENTS,
																companyConfig,
																[role]
															)
																? 'Edit'
																: 'View'}
														</span>
													</div>
												</Menu.Item>
											)
										)}
									</Menu>
								}
								placement="bottomRight"
								trigger={['click']}
							>
								<Button>
									<div className="flex cursor-pointer flex-row items-center space-x-4">
										<div className=" flex items-center gap-x-2">
											<ContactAvatar
												size={16}
												hidePopover={true}
												contact={currentUser}
												userType={userType}
											/>
											{stringArrToList(currentUser.roles.map(userRoleTextFormatter))}
											<span className=" text-sm text-gray-500">
												Can {editable ? 'Edit' : 'View'}
											</span>
										</div>
										<DownOutlined />
									</div>
								</Button>
							</Dropdown>
							<Dropdown
								className="ml-4"
								overlay={
									<Menu>
										<Menu.Item key="pdf" icon={<FilePdfOutlined />} onClick={exportPDF}>
											Export as PDF
										</Menu.Item>
										<Menu.Item key="copy" icon={<LinkOutlined />} onClick={handleCopyLink}>
											Copy Link
										</Menu.Item>
									</Menu>
								}
								placement="bottomRight"
								trigger={['click']}
							>
								<Button icon={<ShareAltOutlined />}>
									Share <DownOutlined />
								</Button>
							</Dropdown>
						</div>
					</div>
				) : null}

				<div
					className="mx-auto max-w-4xl bg-white"
					style={{
						minHeight: '80vh',
						padding: '24px 0',
						fontSize: '14px',
						lineHeight: 1.6,
					}}
					ref={editorRef}
				>
					<div className="bn-shadcn bn-editor mb-4">
						<Input.TextArea
							autoSize
							rows={1}
							disabled={!editable}
							value={title}
							onChange={(e) => {
								setTitle(e.target.value);
								debouncedSave(e.target.value);
							}}
							placeholder="New Document"
							bordered={false}
							autoFocus={isNewDocument}
							className="mb-4 placeholder-gray-400 focus:outline-none"
							style={{
								fontSize: 46,
								lineHeight: '1.25',
								color: '#000000',
							}}
						/>
					</div>
					<BlockNoteView editable={editable} theme="light" editor={editor}></BlockNoteView>
				</div>
			</div>
		</div>
	);
};

export default withUserType(withCurrentUser(withCompanyConfig(BlockNoteEditor)));
